import {PERSONALIZATION_ROUTE} from "account/brand/personalizationRoute";
import {treeRelativePath} from "seo/documentLang";

/**
 * The logged-in navigation MODEL (kitecorp/transferify-ui#332) — which destinations exist, in what
 * order, and which of them an un-activated account may still reach.
 *
 * This lives outside React (epic #26 / `logic-outside-react`) so the availability rule is testable
 * without rendering and survives a rewrite of the bar that shows it. It holds no copy either: callers
 * pass the localized labels in, which is what makes the bar translatable — the table used to carry
 * hardcoded English labels, so `transferify.ro` shipped an English-only nav bar to sighted AND
 * screen-reader users (the visible pill and its `aria-label` are the same string).
 */

/**
 * A destination's stable identity, which is also its label's catalog key for every entry but one:
 * `personalization`'s label has lived at `messages.nav.branding` since ui#450 and the owner kept that
 * key deliberately (see `i18n/en/chrome.ts`). Labels arrive from the caller precisely so where a
 * string lives is not this table's business — {@link ./useAppNavigation} composes the map.
 */
export type DestinationKey = "home" | "transfers" | "contacts" | "personalization" | "account";

/** One app destination, ready to render: the view decides how it looks, never what it offers. */
export interface AppDestination {
	/** The locale-independent identity (catalog key + stable React key). */
	key: DestinationKey;
	label: string;
	path: string;
	disabled: boolean;
}

/**
 * The five logged-in destinations, in BAR order — Personalization sits after Contacts because that is
 * where the pill shows it, and Account stays last because it is the one row no bar surface renders.
 *
 * Reminders left the table in kitecorp/transferify-ui#3140, when file requests became the Requested tab
 * of Transfers: one row here removed it from the pill, the ProfileMenu and the command palette at once.
 *
 * Personalization joined in ui#1292, when the owner gave the top bar's sheet a route of its own. ui#450
 * had kept it out on the grounds that it was "a sheet at no URL"; that premise is gone, so the route
 * model carries it and every table-driven surface — the command palette above all — offers it without
 * a second list to maintain. Two surfaces still render it by hand rather than from this table, and both
 * say so at the render site: the pill is a toggle BUTTON rather than a link, and the ProfileMenu's
 * collapsed row is keyed on the `branding` collapse identity (`navCollapse`), not on this key.
 */
export const DESTINATION_ROUTES: ReadonlyArray<{key: DestinationKey; path: string}> = [
	{key: "home", path: "/"},
	{key: "transfers", path: "/transfers"},
	{key: "contacts", path: "/contacts"},
	// The one path taken from a constant rather than retyped: ui#1292 minted it, and the sheet's own
	// route rules (`isPersonalizationRoute`, `personalizationCloseAction`) read the same one.
	{key: "personalization", path: PERSONALIZATION_ROUTE},
	{key: "account", path: "/account"},
];

/**
 * Whether the page at `pathname` is the destination at `path`: its own path or any path under it, as the
 * Requested tab is under Transfers at /transfers/requested (owner 2026-10-02: "clicking 'Requested' unselects
 * the top 'Transfers'"). Home is home alone, since every path is under `/`. Read inside the language tree,
 * so a storefront subfolder's pages are the same destinations (kitecorp/transferify-ui#3521).
 */
export function isCurrentDestination(pathname: string, path: string): boolean {
	const page = treeRelativePath(pathname);
	if (page === path) return true;
	return path !== "/" && page.startsWith(`${path}/`);
}

/**
 * Access-first (#12): Home and Account stay reachable while the account awaits activation — and so does
 * Personalization, for the reason Account does. It edits ACCOUNT SETTINGS, and what a sender may change
 * inside it is decided by its own plan gate rather than by navigation; the pill has been enabled there
 * since ui#450 and stays enabled now that the pill addresses a route.
 */
const ALWAYS_ENABLED: ReadonlySet<string> = new Set(["/", PERSONALIZATION_ROUTE, "/account"]);

/**
 * Resolves the destinations for an account, labelled from the catalog.
 *
 * `accountActive` is the ACCOUNT activation alone — navigation is never disabled because of the
 * subscription; PRO capabilities are gated in-screen via `gating.planOf` (#12).
 */
export function appDestinations(
	labels: Record<DestinationKey, string>,
	accountActive: boolean,
): AppDestination[] {
	return DESTINATION_ROUTES.map(route => ({
		key: route.key,
		label: labels[route.key],
		path: route.path,
		disabled: !accountActive && !ALWAYS_ENABLED.has(route.path),
	}));
}
